import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout } from '../components/AuthLayout';
import { GoogleButton } from '../components/GoogleButton';
import { getAuthErrorMessage } from '../lib/authErrors';

export function SignupPage() {
    const { signUpWithEmail, signInWithGoogle } = useAuth();
    const navigate = useNavigate();

    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleSignup = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        if (password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères.');
            return;
        }

        setLoading(true);
        try {
            await signUpWithEmail(email.trim(), password, displayName.trim() || undefined);
            // Si la confirmation email est activée dans Supabase, la session
            // ne sera pas créée tant que l'utilisateur n'a pas cliqué le lien.
            // ProtectedRoute redirigera ensuite en fonction de la session réelle.
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(getAuthErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogle = async () => {
        setError(null);
        setLoading(true);
        try {
            // Redirection OAuth : retour auto sur /app/dashboard.
            await signInWithGoogle();
        } catch (err) {
            setError(getAuthErrorMessage(err));
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Créer un compte"
            subtitle="Commence gratuitement, aucun moyen de paiement requis."
            footer={
                <>
                    Déjà inscrit ? <Link to="/login">Se connecter</Link>
                </>
            }
        >
            <GoogleButton onClick={handleGoogle} disabled={loading} label="S'inscrire avec Google" />

            <div className="auth-divider"><span>ou</span></div>

            <form onSubmit={handleSignup} className="auth-form">
                <label className="field">
                    <span className="field-label">Nom (optionnel)</span>
                    <input
                        type="text"
                        autoComplete="name"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Ton nom"
                    />
                </label>

                <label className="field">
                    <span className="field-label">Email</span>
                    <input
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="toi@exemple.com"
                    />
                </label>

                <label className="field">
                    <span className="field-label">Mot de passe</span>
                    <input
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Au moins 6 caractères"
                    />
                </label>

                {error && <div className="auth-error" role="alert">{error}</div>}

                <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Création du compte…' : 'Créer mon compte'}
                </button>
            </form>
        </AuthLayout>
    );
}
